import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Button, DatePicker, Flex, Spin, Typography } from "antd"
import EmbeddedExperienceCard from "../ExperienceCard"
import styles from "./ExperienceListing.module.css"
import EmbeddedBookingLayout from "../EmbeddedBookingLayout"
import { Icon } from "ui/Icon"
import { cleanExperiencesByGuide, fetchExperiencesByGuide } from "store/experiences"
import experiencesSVG from "assets/imagesV2/experiences.svg"
import EmbeddedPageHeader from "../EmbeddedPageHeader"

const { Text, Title } = Typography

const EmbeddedExperienceListing = () => {
  const [t] = useTranslation()
  const { uuid } = useParams()
  const dispatch = useDispatch()
  const { byGuide } = useSelector((state) => state.experiences)
  const [page, setPage] = useState(1)
  const [selectedDate, setSelectedDate] = useState(null)

  const handleSeeMore = () => {
    const nextPage = page + 1
    dispatch(fetchExperiencesByGuide(uuid, selectedDate, nextPage))
    setPage(nextPage)
  }

  const disablePastDates = (current) => {
    return current && current < dayjs().startOf("day")
  }

  useEffect(() => {
    if (uuid) {
      setPage(1)
      dispatch(fetchExperiencesByGuide(uuid))
    }
  }, [uuid, dispatch])

  return (
    <>
      <EmbeddedPageHeader />
      {byGuide?.loading && (
        <div className={styles.loaderOverlay}>
          <Spin size="large" />
        </div>
      )}
      <EmbeddedBookingLayout type={"experience_listing"}>
        <Text className={styles.heading}>{t("webIntegration.choose_your_experience")}</Text>
        <Text className={styles.subheading}>{t("webIntegration.listing_description")}</Text>
        <div style={{ marginTop: "24px" }}>
          <Text className={styles.whenLabel}>{t("webIntegration.when")}</Text>
          <div className={styles.dateSearchBar}>
            <Flex gap={5}>
              <Icon type="feather" name="Calendar" />
              <DatePicker
                style={{ border: "none" }}
                suffixIcon={null}
                value={selectedDate}
                onChange={(value) => setSelectedDate(value)}
                disabledDate={disablePastDates}
                format={"MM/DD/YYYY"}
              />
            </Flex>
            <Button
              type="primary"
              onClick={() => {
                setPage(1)
                const formatted = selectedDate ? dayjs(selectedDate).format("MM-DD-YYYY") : ""
                dispatch(cleanExperiencesByGuide())
                dispatch(fetchExperiencesByGuide(uuid, formatted))
              }}
            >
              {t("webIntegration.search")}
            </Button>
          </div>
        </div>
        <div style={{ marginTop: "40px" }}>
          <Text className={styles.availableHeading}>{t("webIntegration.available_experience")}</Text>
          {byGuide?.data?.length ? (
            <>
              <div className={styles.grid}>
                {byGuide?.data?.map((exp, index) => {
                  const photos = Array.isArray(exp?.photos) ? exp.photos : []
                  const croppedPhotos = Array.isArray(exp?.cropped_photos) ? exp.cropped_photos : []
                  const combinedPhotos = [...photos, ...croppedPhotos]
                  return (
                    <EmbeddedExperienceCard
                      key={index + 1}
                      image={combinedPhotos?.find((item) => item?.position === 0)?.preview_url}
                      title={exp?.title}
                      location={exp?.address}
                      time={exp?.available_hours_range}
                      people={exp?.max_guests}
                      price={exp?.price_per_person}
                      experienceId={exp?.uuid}
                    />
                  )
                })}
              </div>
              {byGuide.data?.length < byGuide?.metaData?.total_results && (
                <div style={{ textAlign: "center", marginTop: "24px" }}>
                  <Button onClick={handleSeeMore} variant="outlined" color="primary">
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {t("webIntegration.see_more")}
                      <Icon type="feather" name="ChevronDown" />
                    </span>
                  </Button>
                </div>
              )}
            </>
          ) : (
            !byGuide?.loading && (
              <div className={styles.emptyBlock}>
                <img src={experiencesSVG} alt="empty" height={"300px"} />
                <Title level={3}>No Data Found.</Title>
              </div>
            )
          )}
        </div>
      </EmbeddedBookingLayout>
    </>
  )
}

export default EmbeddedExperienceListing
